import { GithubIcon } from 'assets/icons'
import { Button, Input, Loading } from 'components'
import {
  useFetchGfgIntegrations,
  useFetchHackerrankIntegrations,
  useFetchLeetcodeIntegrations,
  useFetchUserIntegrations,
  useFetchUserIntegrationsMutation
} from 'features/User'
import dynamic from 'next/dynamic'
import React, { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useQueryClient } from 'react-query'
import { popupWindow } from 'utils'

import { CheckIcon, LoginIcon } from '@heroicons/react/outline'
import { tx } from '@twind/core'
import { API_ENDPOINTS } from 'config/api'

import gfg from '../assets/images/gfg.png'
import hackerrank from '../assets/images/hackerrank.png'
import leetcode from '../assets/images/leetcode.png'
import { User } from '../user.types'

const TotalSolvedQuestionsChart = dynamic(
  () => import('./TotalSolvedQuestionsChart'),
  {
    ssr: false
  }
)

const ActivityMap = dynamic(() => import('./ActivityMap'), {
  ssr: false
})

const AllSourceOfData = [
  {
    id: 'devsnest',
    label: 'Devsnest',
    showInGraph: true,
    icon: null
  },
  {
    id: 'gfg',
    label: 'Geeks for Geeks',
    showInGraph: true,
    icon: <img src={gfg.src} className="h-6 w-6" alt="gfg" />
  },
  {
    id: 'leetcode',
    label: 'Leetcode',
    showInGraph: true,
    icon: <img src={leetcode.src} className="h-6 w-6" alt="leetcode" />
  },

  {
    id: 'hackerrank',
    label: 'Hackerrank',
    showInGraph: true,
    icon: <img src={hackerrank.src} className="h-6 w-6" alt="hackerrank" />
  },
  {
    id: 'github',
    label: 'Github',
    showInGraph: false,
    icon: <GithubIcon className="h-6 w-6" />
  }
] as const

const sourceCodeIDToIndex: Record<string, number> = {
  devsnest: 0,
  leetcode: 1,
  hackerrank: 2,
  gfg: 3
}

const ExternalData = ({
  user,
  isCurrentUser
}: {
  user: User
  isCurrentUser: boolean
}) => {
  const queryClient = useQueryClient()

  const [addProfileData, setAddprofileData] = useState({
    showInput: false,
    profileName: '',
    profileId: '',
    profileUsername: ''
  })
  const { mutateAsync: updateUserExternalProfiles } =
    useFetchUserIntegrationsMutation()

  const { data: externalProfiles, isLoading: isExternalProfilesLoading } =
    useFetchUserIntegrations()

  const { data: leetcodeProfile, isLoading: isLeetcodeProfileLoading } =
    useFetchLeetcodeIntegrations(user?.username || '')

  const { data: gfgProfile, isLoading: isGfgProfileLoading } =
    useFetchGfgIntegrations(user?.username || '')

  const { data: hackerrankProfile, isLoading: ishackerrankProfileLoading } =
    useFetchHackerrankIntegrations(user?.username || '')

  const totalSolvedLeetcode = useMemo(() => {
    let ans = 0

    if (leetcodeProfile && leetcodeProfile.problems_solved) {
      leetcodeProfile.problems_solved?.data?.matchedUser?.submitStatsGlobal?.acSubmissionNum?.forEach(
        (obj) => {
          if (obj.difficulty === 'All') {
            ans = obj.count
          }
        }
      )
    }

    return ans
  }, [leetcodeProfile])

  const totalSolvedGfg = useMemo(() => {
    let ans = 0

    if (gfgProfile && gfgProfile.problems_solved) {
      ans = parseInt(gfgProfile.problems_solved)
    }

    return ans
  }, [gfgProfile])

  const totalSolvedHackerrank = useMemo(() => {
    let ans = 0

    if (hackerrankProfile && hackerrankProfile.profile) {
      ans = hackerrankProfile.profile.languages.reduce((acc, curr) => {
        return (acc += parseInt(curr[1]))
      }, 0)
    }

    return ans
  }, [hackerrankProfile])

  const totalSolvedDevsnest = useMemo(() => {
    let ans = 0

    if (user) {
      ans =
        (user.solved?.easy_level || 0) +
        (user.solved?.medium_level || 0) +
        (user.solved?.hard_level || 0) +
        (user.fe_solved?.html || 0) +
        (user.fe_solved?.css || 0) +
        (user.fe_solved?.javascript || 0) +
        (user.fe_solved?.react || 0)
    }

    return ans
  }, [user])

  const handleAddProfile = () => {
    if (!addProfileData.profileId) {
      toast.error('Something went wrong')
      return
    }

    if (!addProfileData.profileUsername) {
      toast.error('Please Enter Your Username to connect!')
      return
    }

    toast.promise(
      updateUserExternalProfiles({
        [addProfileData.profileId]: addProfileData.profileUsername
      }),
      {
        loading: 'Connecting...',
        success: () => {
          if (addProfileData.profileId === 'gfg_user_name') {
            queryClient.invalidateQueries([
              'gfg-profile',
              { gfgUserName: addProfileData.profileUsername }
            ])
          } else if (addProfileData.profileId === 'leetcode_user_name') {
            queryClient.invalidateQueries([
              'leetcode-profile',
              { leetcodeUserName: addProfileData.profileUsername }
            ])
          } else if (addProfileData.profileId === 'hackerrank_user_name') {
            queryClient.invalidateQueries([
              'hackerrank-profile',
              { hackerrankUserName: addProfileData.profileUsername }
            ])
          }

          setAddprofileData({
            showInput: false,
            profileName: '',
            profileId: '',
            profileUsername: ''
          })

          return 'Profile Connected Successfully!'
        },
        error: (err) => {
          if (err?.response?.data?.data?.attributes?.errors) {
            return `${err?.response?.data?.data?.attributes?.errors[0]}`
          } else if (err?.response?.data?.data?.attributes?.error) {
            return `${err?.response?.data?.data?.attributes?.error?.message}`
          } else {
            return 'An error occurred'
          }
        }
      },
      {
        duration: 4000
      }
    )
  }

  const handleConenctProfileClick = (buttonId: string, buttonLabel: string) => {
    // if (
    //   !(
    //     (buttonId === 'leetcode' && !!externalProfiles?.leetcode_user_name) ||
    //     (buttonId === 'gfg' && !!externalProfiles?.gfg_user_name) ||
    //     (buttonId === 'hackerrank' && !!externalProfiles?.hackerrank_user_name)
    //   )
    // ) {
    //   setAddprofileData((prevData) => ({
    //     ...prevData,
    //     showInput: true,
    //     profileId: `${buttonId}_user_name`,
    //     profileName: buttonLabel
    //   }))
    // }

    if (buttonId !== 'github') {
      setAddprofileData((prevData) => ({
        ...prevData,
        showInput: true,
        profileId: `${buttonId}_user_name`,
        profileName: buttonLabel
      }))
    } else {
      setAddprofileData((prevData) => ({
        ...prevData,
        showInput: false
      }))
      popupWindow(API_ENDPOINTS.GITHUB_LOGIN_REDIRECT, 'test', window, 800, 600)
      // @ts-ignore
      // child call back function
      window.childCallback = (data) => {
        toast.success(data.attributes.message)
      }
    }
  }

  if (isExternalProfilesLoading) return <Loading />

  return (
    <div className="flex w-full flex-col bg-[#1A1A1E] my-4  p-6 gap-4 rounded-sm">
      {isCurrentUser && (
        <div className="flex flex-col gap-4">
          <div className="text-lg font-semibold">Connect</div>
          <div className="flex flex-wrap gap-4">
            {AllSourceOfData.map(
              (source) =>
                source.id !== 'devsnest' && (
                  <div
                    key={source.id}
                    className={tx(
                      `flex items-center justify-between gap-2 px-4 py-2 rounded-sm bg-zinc-700 cursor-pointer hover:bg-zinc-500`
                      // (source.id === 'leetcode' &&
                      //   externalProfiles?.leetcode_user_name) ||
                      //   (source.id === 'gfg' &&
                      //     externalProfiles?.gfg_user_name) ||
                      //   (source.id === 'hackerrank' &&
                      //     externalProfiles?.hackerrank_user_name)
                      //   ? 'cursor-not-allowed'
                      //   : 'cursor-pointer hover:bg-zinc-500'
                    )}
                    onClick={() =>
                      handleConenctProfileClick(source.id, source.label)
                    }>
                    <div className="flex">{source.icon && source.icon}</div>

                    <div className="flex">{source.label}</div>

                    <div className="flex">
                      {(source.id === 'leetcode' &&
                        externalProfiles?.leetcode_user_name) ||
                      (source.id === 'gfg' &&
                        externalProfiles?.gfg_user_name) ||
                      (source.id === 'hackerrank' &&
                        externalProfiles?.hackerrank_user_name) ||
                      (source.id === 'github' && user.github_url) ? (
                        <CheckIcon className="h-6 w-6 text-green-600 font-semibold" />
                      ) : (
                        <LoginIcon className="h-6 w-6 rotate-180 font-semibold" />
                      )}
                    </div>
                  </div>
                )
            )}
          </div>

          <div className="flex flex-col md:flex-row gap-4 mt-4 items-start md:items-end">
            {addProfileData.showInput && (
              <>
                <Input
                  label={`Your ${addProfileData.profileName} username`}
                  id={'input-for-profile-username'}
                  placeholder={`Enter Your ${addProfileData.profileName} username`}
                  className="border-1 border-zinc-500 min-w-[300px]"
                  value={addProfileData.profileUsername}
                  onChange={(e) =>
                    setAddprofileData((prevData) => ({
                      ...prevData,
                      profileUsername: e.target.value
                    }))
                  }
                />

                <div>
                  <Button
                    variant="primary"
                    size="small"
                    onClick={handleAddProfile}>
                    Update Profile
                  </Button>
                </div>

                <div>
                  <Button
                    size="small"
                    variant="danger"
                    onClick={() =>
                      setAddprofileData({
                        showInput: false,
                        profileName: '',
                        profileId: '',
                        profileUsername: ''
                      })
                    }>
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2 w-full my-4">
        {isLeetcodeProfileLoading ||
        isGfgProfileLoading ||
        ishackerrankProfileLoading ? (
          <Loading />
        ) : (
          <TotalSolvedQuestions
            totalSolvedDataArray={[
              totalSolvedDevsnest,
              totalSolvedLeetcode,
              totalSolvedHackerrank,
              totalSolvedGfg
            ]}
          />
        )}
      </div>

      <ActivityMap user={user} />
    </div>
  )
}

export default ExternalData

const TotalSolvedQuestions = ({
  totalSolvedDataArray
}: {
  totalSolvedDataArray: Array<number>
}) => {
  return (
    <>
      <div className="flex flex-col gap-4 my-2">
        <div className="text-lg font-semibold">Total Solved Questions</div>
        <div className="flex flex-col md:flex-row gap-4">
          {AllSourceOfData.map(
            (source) =>
              source.showInGraph && (
                <div
                  key={source.id}
                  className={`flex px-4 py-2 rounded-sm border-1 border-profile-${source.id}`}>
                  {source.label}:{' '}
                  {totalSolvedDataArray[sourceCodeIDToIndex[source.id] || 0]}
                </div>
              )
          )}
        </div>
      </div>

      <TotalSolvedQuestionsChart totalSolvedDataArray={totalSolvedDataArray} />
    </>
  )
}
